import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import styles from "./ProjectsFilter.module.css";

const DRIVE_BASE_URL = "https://www.googleapis.com/drive/v3/files";
const CACHE_KEY = "projects_dp_cache_v1";
const CACHE_MAX_AGE_MS = 24 * 60 * 60 * 1000;
const REVALIDATE_AGE_MS = 10 * 60 * 1000;
const DP_REQUEST_CONCURRENCY = 3;
const RETRY_DELAYS_MS = [400, 900];

const toProjectKey = (category, title) =>
  `${String(category).toLowerCase()}::${String(title).toLowerCase()}`;

const getDpFile = (files = []) => {
  if (!files.length) return null;
  const namedDp = files.find((file) =>
    file.name.toLowerCase().includes("dp")
  );
  return namedDp || files[0];
};

const toThumbUrl = (fileId, modifiedTime) =>
  `https://drive.google.com/thumbnail?id=${fileId}&sz=w2000&v=${encodeURIComponent(
    modifiedTime || ""
  )}`;

const readCache = () => {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    if (!parsed?.savedAt || !Array.isArray(parsed.projects)) return null;
    return parsed;
  } catch (error) {
    console.log("Projects cache read error:", error);
    return null;
  }
};

const writeCache = (payload) => {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(payload));
  } catch (error) {
    console.log("Projects cache write error:", error);
  }
};

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const mapWithConcurrency = async (items, worker, concurrency) => {
  if (!items.length) return [];
  const results = new Array(items.length);
  let index = 0;

  const run = async () => {
    while (true) {
      const currentIndex = index;
      index += 1;
      if (currentIndex >= items.length) break;
      results[currentIndex] = await worker(items[currentIndex], currentIndex);
    }
  };

  const runners = Array.from(
    { length: Math.min(concurrency, items.length) },
    () => run()
  );
  await Promise.all(runners);
  return results;
};

const Projects = () => {
  const [folders, setFolders] = useState([]);
  const [allProjects, setAllProjects] = useState([]);
  const [active, setActive] = useState(
    sessionStorage.getItem("activeProjectFilter") || "All"
  );
  const [loading, setLoading] = useState(true);

  const navigate = useNavigate();

  // GOOGLE DRIVE CONFIG
  const mainFolderId = "1se1WvZ297MXYbBHjl8qxIpvrRgJCv35b";
  const apiKey = "AIzaSyAAgFvKs3Vo5nuo_bkIY1ePkNqNsSmB33g";

  const driveFetch = async (q, fields, retryAttempt = 0) => {
    try {
      const res = await axios.get(DRIVE_BASE_URL, {
        params: {
          q,
          fields,
          pageSize: 1000,
          key: apiKey,
        },
      });
      return res.data.files || [];
    } catch (error) {
      const status = error?.response?.status;
      const shouldRetry =
        (status === 403 || status === 429) &&
        retryAttempt < RETRY_DELAYS_MS.length;
      if (shouldRetry) {
        await sleep(RETRY_DELAYS_MS[retryAttempt]);
        return driveFetch(q, fields, retryAttempt + 1);
      }
      throw error;
    }
  };

  const slugify = (text) => text.toLowerCase().replace(/\s+/g, "-").trim();

  const displayProjects = useMemo(() => {
    return allProjects.map((project) => ({
      category: project.category,
      title: project.title,
      img: project.img,
      id: project.id,
    }));
  }, [allProjects]);

  const filteredProjects = useMemo(() => {
    if (active === "All") return displayProjects;
    return displayProjects.filter(
      (project) => project.category.toLowerCase() === active.toLowerCase()
    );
  }, [active, displayProjects]);

  useEffect(() => {
    let cancelled = false;

    const fetchFreshProjects = async (cachedMap) => {
      const categoryFolders = await driveFetch(
        `'${mainFolderId}' in parents and mimeType='application/vnd.google-apps.folder'`,
        "files(id,name)"
      );

      if (!cancelled) {
        setFolders(categoryFolders);
      }

      const categoryResults = await Promise.all(
        categoryFolders.map(async (categoryFolder) => {
          const projectFolders = await driveFetch(
            `'${categoryFolder.id}' in parents and mimeType='application/vnd.google-apps.folder'`,
            "files(id,name)"
          );

          if (!projectFolders.length) return [];

          const projectItems = await mapWithConcurrency(
            projectFolders,
            async (projectFolder) => {
              let imageFiles = [];
              try {
                imageFiles = await driveFetch(
                  `'${projectFolder.id}' in parents and mimeType contains 'image/'`,
                  "files(id,name,modifiedTime)"
                );
              } catch (error) {
                // Keep page usable even if one folder hits API restrictions.
                return null;
              }

              const dpFile = getDpFile(imageFiles);
              if (!dpFile) return null;

              const key = toProjectKey(categoryFolder.name, projectFolder.name);
              const cached = cachedMap.get(key);
              const isChanged =
                !cached ||
                cached.dpFileId !== dpFile.id ||
                cached.modifiedTime !== (dpFile.modifiedTime || "");

              return {
                key,
                category: categoryFolder.name,
                title: projectFolder.name,
                id: dpFile.id,
                dpFileId: dpFile.id,
                modifiedTime: dpFile.modifiedTime || "",
                img:
                  !isChanged && cached?.img
                    ? cached.img
                    : toThumbUrl(dpFile.id, dpFile.modifiedTime || ""),
              };
            },
            DP_REQUEST_CONCURRENCY
          );

          return projectItems.filter(Boolean);
        })
      );

      return {
        folders: categoryFolders,
        projects: categoryResults.flat(),
      };
  };

    const init = async () => {
      const cache = readCache();
      const cacheProjects = cache?.projects || [];
      const cacheFolders = cache?.folders || [];
      const cacheAge = cache?.savedAt ? Date.now() - cache.savedAt : Infinity;
      const hasFreshCache = cacheAge <= CACHE_MAX_AGE_MS && cacheProjects.length;

      if (hasFreshCache && !cancelled) {
        setFolders(cacheFolders);
        setAllProjects(cacheProjects);
        setLoading(false);
      }

      if (hasFreshCache && cacheAge <= REVALIDATE_AGE_MS) {
        return;
      }

      const cachedMap = new Map(
        cacheProjects.map((project) => [project.key, project])
      );

      try {
        const fresh = await fetchFreshProjects(cachedMap);
        const sortedProjects = fresh.projects.sort((a, b) =>
          a.title.localeCompare(b.title)
        );

        const cachePayload = {
          savedAt: Date.now(),
          folders: fresh.folders,
          projects: sortedProjects,
        };
        writeCache(cachePayload);

        if (!cancelled) {
          setFolders(fresh.folders);
          setAllProjects(sortedProjects);
          setLoading(false);
        }
      } catch (error) {
        console.log("Project load error:", error);
        if (!cancelled) {
          if (cacheProjects.length) {
            setFolders(cacheFolders);
            setAllProjects(cacheProjects);
          }
          setLoading(false);
        }
      }
    };

    init();

    return () => {
      cancelled = true;
    };
  }, []);

  const handleFilter = (cat) => {
    setActive(cat);
    sessionStorage.setItem("activeProjectFilter", cat);
  };

  return (
    <div className={styles.projectSection}>
      <div className="container" >
        {/* <h2 className={styles.heading}>PROJECTS</h2>
        <p className={styles.subtext}>
          Our portfolio showcases a diverse range of projects, each echoing our commitment to client-oriented solutions, efficient space use, and harmony between architecture and interior design.
        </p> */}

        {/* FILTER BUTTONS */}
        <div className={styles.filterWrapper}>
          <button
            className={`${styles.filterBtn} ${active === "All" ? styles.active : ""
              }`}
            onClick={() => handleFilter("All")}
          >
            All
          </button>

          {folders.map((folder) => (
            <button
              key={folder.id}
              className={`${styles.filterBtn} ${active === folder.name ? styles.active : ""
                }`}
              onClick={() => handleFilter(folder.name)}
            >
              {folder.name}
            </button>
          ))}
        </div>

        {/* LOADING */}


        {/* PROJECT GRID */}
        {!loading && (
          <div className={styles.grid}>
            {filteredProjects.map((item) => (
              <div
                key={`${item.category}-${item.title}`}
                className={styles.card}
                onClick={() =>
                  navigate(`/projects/${slugify(item.title)}`)
                }
              >
                <div className={styles.imgWrapper}>
                  <img
                    src={item.img}
                    alt={item.title}
                    loading="lazy"
                    decoding="async"
                    className={styles.image}
                  />
                </div>

                <div className={styles.overlay}>
                  <h3>{item.title}</h3>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Projects;
